import { useState, useContext, useEffect, useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useField, useFormikContext } from "formik";
import dayjs from "dayjs";
import _ from "lodash";

import { Grid } from "@mui/material";
import { Divider } from "@mui/material";
import MyTypography from "../../../components/Typography";
import Select from "../../../components/Select";
import BubbleSelect from "../../../components/Selects/BubbleSelect";
import DatePicker from "../../../components/DateTimePicker/DatePicker";
import EditBubbleCheckbox from "../../../components/Checkboxs/EditAddressCheckbox";
import Space from "../../../components/Space";
import Checkbox from "../../../components/Checkbox";
import EditAddressDialog from "../userInfo/EditAddressDialog";
import AddIcon from "../../../assets/ic-add.svg";

import { GlobalContextStore } from "../../../context/GlobalProvider";
import { disableSunday, formatCurrency } from "../../../utils/common";
import { WindowSize, breakpoint } from "../../../utils/responsive";
import theme from "../../../theme";

const PICKUP_LIST = [
  {
    _id: "pickup_cwd",
    title: "cwbSelfPick",
    value: "cwbSelfPick",
    address: ["香港銅鑼灣商業客戶服務中心", "XXX樓XX室"],
    district: "wanChai",
  },
];

export const TIME_SLOT = [
  {
    title: "9am - 1pm",
    value: "am",
  },
  {
    title: "2pm - 6pm",
    value: "pm",
  },
];

const DeliveryInformation = ({
  viewOnly,
  setSkipValidate,
  configData,
  calendarData,
  allowDelivery = false,
  allowPickup = false,
  hasDevice = false,
}) => {
  const { t, i18n } = useTranslation(["checkout", "error"]);
  const [isAddressDialogOpen, setAddressDialogOpen] = useState(false);
  const [disablePm, setDisablePm] = useState(false);
  const { globalState, globalDispatch } = useContext(GlobalContextStore);
  const [field, meta, helper] = useField("delivery_method");
  const { values, errors, setValues, setFieldValue, submitForm } = useFormikContext();

  const isMobile = WindowSize() === "mobile";

  // Disable pm timeslot for sat
  useEffect(() => {
    if (dayjs(values.delivery_date, "DD/MM/YYYY").day() === 6) {
      setDisablePm(true);
      if (values.delivery_time === "pm") {
        setFieldValue("delivery_time", "");
      }
    } else setDisablePm(false);
  }, [values.delivery_date]);

  // default choose address if only one address
  useEffect(() => {
    const shippingMethod = field.value;

    if (shippingMethod === "delivery") {
      const isSameAsMailingAddress = values?.delivery_isSameAsMailingAddress;

      // Case when delivery address same as mailing address
      if (isSameAsMailingAddress) {
        setValues({
          ...values,
          delivery_address1: values?.customer_address1,
          delivery_address2: values?.customer_address2,
          delivery_district: values?.customer_district,
          delivery_addressId: "",
        });
      } else {
        // Case when delivery address not same as mailing address
        // Use user-stored mailing address if available, else use empty string
        const userShippingAddress = globalState?.userInfo?.shippingAddresses?.[0];
        setValues({
          ...values,
          delivery_address1: userShippingAddress?.address[0] || "",
          delivery_address2: userShippingAddress?.address[1] || "",
          delivery_district: userShippingAddress?.district || "",
          delivery_addressId: userShippingAddress?._id || "",
        })
      }

    } else {
      // TODO: handle pickup
    }
  }, [field.value, values.delivery_isSameAsMailingAddress]);

  const handleChangeMethod = (isChanged) => {
    //dont clear field when select same option
    if (isChanged) {
      setValues({
        ...values,
        delivery_addressId: "",
        delivery_address1: "",
        delivery_address2: "",
        delivery_district: "",
        delivery_date: "",
      });
    }
  };

  const handleAutoSubmit = () => {};

  const _renderError = () => {
    return errors?.delivery_address1 ? (
      <Grid item xs={12} marginTop={"-20px"} marginLeft={"30px"}>
        <MyTypography fontSize={12} color={theme.error2}>
          {t(errors?.delivery_address1)}
        </MyTypography>
      </Grid>
    ) : null;
  };

  const _renderPickUp = () => {
    return viewOnly ? (
      _renderPreviewAddress()
    ) : (
      <Grid container item xs={12} spacing={2} paddingX={{ xs: "25px", md: "30px" }}>
        {PICKUP_LIST.map((item) => {
          return (
            <Grid item xs={12} md={4}>
              <EditBubbleCheckbox
                disableEdit
                handleAutoSubmit={handleAutoSubmit}
                checked={field.value === "pickup"}
                addressId={item._id}
                addressOne={item.address[0]}
                addressTwo={item.address[1]}
                district={item.district}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const _renderDelivery = () => {
    return viewOnly ? (
      _renderPreviewAddress()
    ) : (
      <Grid container item xs={12} spacing={2} paddingX={{ xs: "25px", md: "30px" }}>
        <Grid item xs={12}>
          <Checkbox
            id="delivery_isSameAsMailingAddress"
            label={t("sameAsAddress")}
          />
        </Grid>
        {!values.delivery_isSameAsMailingAddress && (
          <Grid container item spacing={2}>
            {globalState?.userInfo?.shippingAddresses?.map((item) => {
              return (
                <Grid item xs={12} md={4}>
                  <EditBubbleCheckbox
                    handleAutoSubmit={handleAutoSubmit}
                    addressId={item._id}
                    setIsOpen={setAddressDialogOpen}
                    addressOne={item.address[0]}
                    addressTwo={item.address[1]}
                    district={item.district}
                    handleDelete={() => submitForm()}
                  />
                </Grid>
              );
            })}

            {(!globalState?.userInfo?.shippingAddresses ||
              globalState?.userInfo?.shippingAddresses?.length < 3) && (
                <Grid item xs={12} md={4}>
                  <div name="delivery_address1" />
                  <AddNewAddress onClick={() => setAddressDialogOpen(true)}>
                    <img src={AddIcon} />
                    <Space size="20px" />
                    <MyTypography fontSize="16">
                      {t("addNewAddress")}
                    </MyTypography>
                  </AddNewAddress>
                </Grid>
              )}
          </Grid>
        )}

        <EditAddressDialog
          isOpen={isAddressDialogOpen}
          setIsOpen={setAddressDialogOpen}
          handleAutoSubmit={handleAutoSubmit}
        />
      </Grid>
    );
  };

  const _renderPreviewAddress = () => {
    return (
      <div style={{ marginLeft: 30, marginTop: 30 }}>
        <MyTypography fontSize="16">
          {t("address")}
          <span style={{ color: theme.orange, marginLeft: "5px" }}>*</span>
        </MyTypography>

        <Space size="8px" />
        <MyTypography fontSize="18">{values?.delivery_address1}</MyTypography>
        <MyTypography fontSize="18">{values?.delivery_address2}</MyTypography>
        <Space size="30px" />
        <MyTypography fontSize="16">
          {t("district")}
          <span style={{ color: theme.orange, marginLeft: "5px" }}>*</span>
        </MyTypography>
        <Space size="8px" />
        <MyTypography fontSize="18">
          {t(values?.delivery_district)}
        </MyTypography>
      </div>
    );
  };

  const disableHoliday = (date, excludeSat = false) => {
    const formatedDate = dayjs(date).format("DD/MM/YYYY");
    const day = date.day();
    const nonShipDay = day === 0 || calendarData?.includes(formatedDate);
    const nonWorkingDay = day === 0 || day === 6 || calendarData?.includes(formatedDate);

    return excludeSat ? nonWorkingDay : nonShipDay;
  };

  const getMinData = () => {
    const keyPath =
      field.value.indexOf("pickup") !== -1
        ? "delivery.estimatePickupDay"
        : "delivery.estimatedDeliveryDay";
    let remain = _.find(configData, ["keyPath", keyPath])?.value;
    let minDate = dayjs(Date.now());

    while (remain > 0) {
      minDate = minDate.add(1, "day");

      if (!disableHoliday(minDate, true)) {
        remain--;
      }
    }

    return minDate;
  };

  const deliveryMinSpent = _.find(configData, ["keyPath", "delivery.freeShippingMinSpent"])?.value;
  const deliveryFee = _.find(configData, ["keyPath", "delivery.deliveryFee"])?.value;

  const availableDeliveryMethod = useMemo(() => {
    const options = [];
    
    // if (allowPickup) {
    //   options.push({
    //     id: "pickup",
    //     label: t("cwbSelfPick"),
    //     tag: t("freeCashCoupon"),
    //     value: "pickup",
    //   });
    // }
    // SECURITY: Pick Up is disabled system-wide. The option is intentionally NOT
    // rendered, even if the quote API response returns allowPickup=true (e.g. a
    // tampered / mocked response). The backend also rejects any pickup delivery
    // method at quote update and order creation.
    // Pickup option intentionally removed:
    // if (allowPickup) { options.push({ id: "pickup", value: "pickup", ... }); }

    if (allowDelivery) {
      options.push({ 
        id: "delivery",
        label: t(hasDevice ? "deliveryWithDevice" : "delivery", { 
          minSpent: formatCurrency(deliveryMinSpent, 0),
          deliveryFee: formatCurrency(deliveryFee, 0),
        }),
        value: "delivery",
      });
    }

    return options;
  }, [allowPickup, allowDelivery, hasDevice, i18n.language, t, deliveryFee, deliveryMinSpent])

  return (
    <>
      <Grid item xs style={{ padding: `0 ${isMobile ? 25 : 30}px` }}>
        <BubbleSelect
          viewOnly={viewOnly}
          id="delivery_method"
          onChange={handleChangeMethod}
          options={availableDeliveryMethod}
          bubbleWidth={"350px"}
          disableUnchecked
          verticalAlign={isMobile}
          typographyProps={{
            style: {
              whiteSpace: "pre-line",
            }
          }}
        />
      </Grid>

      <Grid item xs={12} paddingY={"30px"}>
        <Divider />
      </Grid>

      <Grid item xs={12} style={{ padding: `0 ${isMobile ? 25 : 30}px` }}>
        <MyTypography fontSize="24" bold>
          {t("shippingAddress")}
        </MyTypography>
      </Grid>

      {field.value.indexOf("pickup") !== -1
        ? _renderPickUp()
        : field.value.indexOf("delivery") !== -1
          ? _renderDelivery()
          : null}

      {_renderError()}

      <Grid item xs={12} style={{ padding: `30px ${isMobile ? 25 : 30}px 0` }}>
        <DatePicker
          id="delivery_date"
          name="delivery_date"
          title={t("timeOfReceipt")}
          viewOnly={viewOnly}
          minDate={getMinData()}
          shouldDisableDate={disableHoliday}
          required
        />
        {!viewOnly && <Space size="15px" />}
        <Select
          id="delivery_time"
          options={disablePm ? [TIME_SLOT[0]] : TIME_SLOT}
          viewOnly={viewOnly}
          required
          hintText={t("selectTime")}
        />
      </Grid>
    </>
  );
};

const AddNewAddress = styled.div`
  cursor: pointer;
  border: 2px dashed #c9c9c9;
  border-radius: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;

  @media ${breakpoint.desktop} {
    height: 98%;
    min-height: 180px;
  }

  @media ${breakpoint.mobile} {
    height: 200px;
  }
`;

export default DeliveryInformation;
